import React, { useRef, useEffect, useState } from "react";
import { toast } from "sonner";
import { Bold, Italic, Underline, List, ListOrdered, Heading2, Heading3, Link2, Quote, Eraser, ImagePlus, Upload, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const exec = (command, value = null) => document.execCommand(command, false, value);
const fullUrl = (u) => (u?.startsWith("http") ? u : `${BACKEND}${u}`);

export default function RichTextEditor({ value, onChange, testid }) {
  const ref = useRef(null);
  const savedRange = useRef(null);
  const fileRef = useRef(null);
  const [mediaOpen, setMediaOpen] = useState(false);
  const [media, setMedia] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);

  useEffect(() => {
    if (ref.current && ref.current.innerHTML !== (value || "")) {
      ref.current.innerHTML = value || "";
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const sync = () => onChange(ref.current?.innerHTML || "");

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current?.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  const restoreSelection = () => {
    ref.current?.focus();
    if (savedRange.current) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(savedRange.current);
    }
  };

  const run = (command, val) => {
    ref.current?.focus();
    exec(command, val);
    sync();
  };

  const addLink = () => {
    const url = window.prompt("Enter the URL (https://…)");
    if (url) run("createLink", url);
  };

  const openMedia = () => {
    saveSelection();
    api.get("/media").then((r) => setMedia(r.data)).catch(() => setMedia([]));
    setMediaOpen(true);
  };

  const insertImage = (url) => {
    restoreSelection();
    exec("insertImage", fullUrl(url));
    sync();
  };

  const uploadFile = async (file) => {
    if (!file || !file.type?.startsWith("image/")) { toast.error("Please choose an image file"); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const r = await api.post("/media", fd);
      insertImage(r.data.url);
      toast.success("Image uploaded & inserted");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const files = Array.from(e.target.files || []);
    files.forEach(uploadFile);
    if (fileRef.current) fileRef.current.value = "";
  };

  const triggerUpload = () => { saveSelection(); fileRef.current?.click(); };

  const onDrop = (e) => {
    const files = Array.from(e.dataTransfer?.files || []).filter((f) => f.type.startsWith("image/"));
    if (!files.length) return;
    e.preventDefault();
    setDragging(false);
    // place caret at drop point so the image lands where dropped
    const range = document.caretRangeFromPoint?.(e.clientX, e.clientY);
    if (range) { const sel = window.getSelection(); sel.removeAllRanges(); sel.addRange(range); saveSelection(); }
    else saveSelection();
    files.forEach(uploadFile);
  };

  const Btn = ({ icon: Icon, onClick, title, disabled }) => (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onMouseDown={(e) => { e.preventDefault(); if (!disabled) onClick(); }}
      className="p-2 rounded text-slate-300 hover:text-[#D8AE4E] hover:bg-[#102653] transition-colors disabled:opacity-40"
    >
      <Icon size={15} />
    </button>
  );

  return (
    <div className="border border-[#203E70] rounded overflow-hidden bg-[#06122E]">
      <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickFile} data-testid="rte-upload-input" />
      <div className="flex flex-wrap items-center gap-0.5 border-b border-[#203E70] bg-[#081734] px-1.5 py-1" data-testid="rte-toolbar">
        <Btn icon={Bold} title="Bold" onClick={() => run("bold")} />
        <Btn icon={Italic} title="Italic" onClick={() => run("italic")} />
        <Btn icon={Underline} title="Underline" onClick={() => run("underline")} />
        <span className="w-px h-5 bg-[#203E70] mx-1" />
        <Btn icon={Heading2} title="Heading" onClick={() => run("formatBlock", "H2")} />
        <Btn icon={Heading3} title="Subheading" onClick={() => run("formatBlock", "H3")} />
        <Btn icon={Quote} title="Quote" onClick={() => run("formatBlock", "BLOCKQUOTE")} />
        <span className="w-px h-5 bg-[#203E70] mx-1" />
        <Btn icon={List} title="Bulleted list" onClick={() => run("insertUnorderedList")} />
        <Btn icon={ListOrdered} title="Numbered list" onClick={() => run("insertOrderedList")} />
        <Btn icon={Link2} title="Insert link" onClick={addLink} />
        <span className="w-px h-5 bg-[#203E70] mx-1" />
        <Btn icon={uploading ? Loader2 : Upload} title="Upload image" onClick={triggerUpload} disabled={uploading} />
        <Btn icon={ImagePlus} title="Insert image from Media Library" onClick={openMedia} />
        <span className="w-px h-5 bg-[#203E70] mx-1" />
        <Btn icon={Eraser} title="Clear formatting" onClick={() => run("removeFormat")} />
        {uploading && <span className="text-xs text-slate-400 ml-2 inline-flex items-center gap-1"><Loader2 size={12} className="animate-spin" /> Uploading…</span>}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={sync}
        onBlur={() => { saveSelection(); sync(); }}
        onKeyUp={saveSelection}
        onMouseUp={saveSelection}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        data-testid={testid}
        className={`cb-prose min-h-[220px] max-h-[45vh] overflow-y-auto admin-scroll px-4 py-3 text-sm text-slate-200 outline-none transition-colors ${dragging ? "ring-2 ring-[#D8AE4E]/60 ring-inset bg-[#0B1D40]" : ""}`}
      />
      <div className="border-t border-[#203E70] bg-[#081734] px-3 py-1.5 text-[11px] text-slate-500">Drag &amp; drop an image here, or use the upload button.</div>

      <Dialog open={mediaOpen} onOpenChange={setMediaOpen}>
        <DialogContent className="bg-[#0B1D40] border-[#203E70] text-white max-w-2xl max-h-[80vh] overflow-y-auto admin-scroll">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg">Insert Image</DialogTitle>
            <DialogDescription className="text-slate-400 text-sm">Click an image from your Media Library to insert it at the cursor.</DialogDescription>
          </DialogHeader>
          {media.length === 0 ? (
            <p className="text-slate-500 text-sm py-8 text-center" data-testid="rte-media-empty">No media uploaded yet. Use the Upload button in the toolbar, or add images in Admin → Media Library.</p>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
              {media.map((m) => (
                <button
                  key={m.id}
                  type="button"
                  onClick={() => { setMediaOpen(false); insertImage(m.url); }}
                  className="aspect-square rounded overflow-hidden border border-[#203E70] hover:border-[#D8AE4E] transition-colors"
                  title={m.filename}
                  data-testid={`rte-media-${m.id}`}
                >
                  <img src={fullUrl(m.url)} alt={m.filename} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
